import config from 'config';
import { ListPayload, ListResponse, SuccessResponse } from 'core/http/response';
import axiosClient from '../../config/axios';
import {
    AddJobNotePayload,
    CreateUpdateJobNoteResponse,
    CreateJobPayload,
    DeclarationJobInfo,
    GetJobHistoryResponse,
    GetJobHistoryParams,
    GetJobNoteParams,
    GetJobNotesResponse,
    GetJobsParams,
    JobHistory,
    JobNote,
    JobResponse,
    CreateDocumentPayload,
    JobDocumentResponse,
    ListDocumentsParams,
    ListDocumentsResponse,
    JobDocument,
    GetDocumentUploadUrlParams,
    GetDocumentUploadUrlResponse,
    GetDocumentDownloadUrlResponse,
    DocumentPresignedUrlData,
    UpdateExternalReferencesPayload,
} from './job';
import { AxiosResponse } from 'axios';
import {
    EditJobPayload,
    EmailJobDocumentsResponse,
    EmailStatusResponse,
    SendJobsEmailToCustomerResponse,
} from './types';

export const createJob = (payload: CreateJobPayload): Promise<JobResponse> =>
    axiosClient.post<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs`, payload).then((response) => {
        return response.data.payload;
    });

export const listJobs = (
    params: GetJobsParams,
    options?: { signal?: AbortSignal }
): Promise<ListPayload<JobResponse>> =>
    axiosClient
        .get<ListResponse<JobResponse>>(`${config.jobsUrl}/jobs`, { params, signal: options?.signal })
        .then((response) => {
            return response.data.payload;
        });

export const listJobsWithDeclarations = (params: {
    page: number;
    size: number;
}): Promise<ListPayload<DeclarationJobInfo>> => {
    return axiosClient
        .get<ListResponse<DeclarationJobInfo>>(`${config.declarationsUrl}/declarations/job/list/info`, { params })
        .then((response) => {
            return response.data.payload;
        });
};

export const getJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.get<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}`).then((response) => {
        return response.data.payload;
    });

export const editJob = (jobId: string, payload: EditJobPayload): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}`, payload).then((response) => {
        return response.data.payload;
    });

export const listTags = (params?: any): Promise<string[]> =>
    axiosClient.get<string[]>(`${config.jobsUrl}/jobs/tags`, { params }).then((response) => {
        return response.data;
    });

export const deleteJob = (jobId: string): Promise<void> =>
    axiosClient.delete<SuccessResponse<void>>(`${config.jobsUrl}/jobs/${jobId}`).then((response) => {
        return response.data.payload;
    });

export const assignJob = (jobId: string, job: JobResponse): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/assign`, job).then((response) => {
        return response.data.payload;
    });

// Assign CLEARANCE_AGENT to job
export const assignOwnerToJob = async (jobId: string, individualId: string): Promise<JobResponse> => {
    const response: AxiosResponse<SuccessResponse<JobResponse>> = await axiosClient.patch(
        `${config.jobsUrl}/jobs/${jobId}/assign-owner/${individualId}`
    );
    return response.data.payload;
};

export const closeJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/close`).then((response) => {
        return response.data.payload;
    });

export const acceptJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/accept`).then((response) => {
        return response.data.payload;
    });

export const startJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/start`).then((response) => {
        return response.data.payload;
    });

export const rejectJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/reject`).then((response) => {
        return response.data.payload;
    });

export const completeJob = (jobId: string): Promise<JobResponse> =>
    axiosClient.put<SuccessResponse<JobResponse>>(`${config.jobsUrl}/jobs/${jobId}/complete`).then((response) => {
        return response.data.payload;
    });

export const archiveJob = async (jobId: string): Promise<JobResponse> => {
    const response: AxiosResponse<SuccessResponse<JobResponse>> = await axiosClient.patch(
        `${config.jobsUrl}/jobs/archive/${jobId}`
    );
    return response.data.payload;
};

export const unarchiveJob = async (jobId: string): Promise<JobResponse> => {
    const response: AxiosResponse<SuccessResponse<JobResponse>> = await axiosClient.patch(
        `${config.jobsUrl}/jobs/unarchive/${jobId}`
    );
    return response.data.payload;
};

export const deleteJobNote = async (jobId: string, noteId: string): Promise<{}> => {
    const response: AxiosResponse<SuccessResponse<{}>> = await axiosClient.delete(
        `${config.jobsUrl}/jobs/${jobId}/notes/${noteId}`
    );
    return response.data.payload;
};

export const updateJobNote = async (
    jobId: string,
    noteId: string,
    payload: {
        title: string;
        note: string;
    }
): Promise<CreateUpdateJobNoteResponse> => {
    const response: AxiosResponse<SuccessResponse<CreateUpdateJobNoteResponse>> = await axiosClient.put(
        `${config.jobsUrl}/jobs/${jobId}/notes/${noteId}`,
        payload
    );
    return response.data.payload;
};

export const addNoteToJob = async (jobId: string, payload: AddJobNotePayload): Promise<CreateUpdateJobNoteResponse> => {
    const response: AxiosResponse<CreateUpdateJobNoteResponse> = await axiosClient.post(
        `${config.jobsUrl}/jobs/${jobId}/notes`,
        payload
    );
    return response.data;
};

export const getJobNotes = async (jobId: string, params?: GetJobNoteParams): Promise<ListPayload<JobNote>> => {
    const response: AxiosResponse<GetJobNotesResponse> = await axiosClient.get(
        `${config.jobsUrl}/jobs/${jobId}/notes`,
        {
            params,
        }
    );
    return response.data.payload;
};

export const createDocument = async (jobId: string, payload: CreateDocumentPayload): Promise<JobDocumentResponse> => {
    const response = await axiosClient.post(`${config.jobsUrl}/jobs/documents/${jobId}`, payload);
    return response.data;
};

export const deleteDocument = async (
    jobId: string,
    documentId: string
): Promise<{
    category: string;
    code: string;
    message: string;
    payload: {};
}> => {
    const response = await axiosClient.delete(`${config.jobsUrl}/jobs/documents/${jobId}/delete/${documentId}`);
    return response.data;
};

export const listDocumentsByJobId = async (
    jobId: string,
    params: ListDocumentsParams
): Promise<ListPayload<JobDocument>> => {
    const response: AxiosResponse<ListDocumentsResponse> = await axiosClient.post(
        `${config.jobsUrl}/jobs/documents/list/${jobId}`,
        {},
        {
            params,
        }
    );

    return response.data.payload;
};

export const getDocumentUploadUrl = async (
    jobId: string,
    params: GetDocumentUploadUrlParams
): Promise<DocumentPresignedUrlData> => {
    const response: AxiosResponse<GetDocumentUploadUrlResponse> = await axiosClient.get(
        `${config.jobsUrl}/jobs/documents/upload/${jobId}`,
        {
            params,
        }
    );
    return response.data.payload;
};

export const getDocumentDownloadUrl = async (jobId: string, documentId: string): Promise<DocumentPresignedUrlData> => {
    const response: AxiosResponse<GetDocumentDownloadUrlResponse> = await axiosClient.get(
        `${config.jobsUrl}/jobs/documents/${jobId}/download/${documentId}`
    );
    return response.data.payload;
};

export const getHistoryByJobId = async (
    jobId: string,
    params?: GetJobHistoryParams
): Promise<ListPayload<JobHistory>> => {
    const response: AxiosResponse<GetJobHistoryResponse> = await axiosClient.get(
        `${config.jobsUrl}/jobs/${jobId}/history`,
        {
            params,
        }
    );
    return response.data.payload;
};

// Get only jobs that have linked invoices
export const getJobsWithInvoices = async (params?: GetJobsParams): Promise<ListPayload<JobResponse>> => {
    const response: AxiosResponse<SuccessResponse<ListPayload<JobResponse>>> = await axiosClient.get(
        `${config.jobsUrl}/jobs/list/invoice-jobs`,
        { params }
    );

    return response.data.payload;
};

export const updateExternalReferences = async (
    jobId: string,
    payload: UpdateExternalReferencesPayload
): Promise<JobResponse> => {
    const response: AxiosResponse<SuccessResponse<JobResponse>> = await axiosClient.patch(
        `${config.jobsUrl}/jobs/${jobId}/external-references`,
        payload
    );
    return response.data.payload;
};

// Get the information for released documents for jobs
export const getEmailJobsDocuments = async (
    manifestId: string,
    jobIds?: string[]
): Promise<EmailJobDocumentsResponse> => {
    const response: AxiosResponse<SuccessResponse<any>> = await axiosClient.get(
        `${config.declarationsUrl}/manifest/${manifestId}/email-job-documents`,
        {
            params: { jobIds },
            paramsSerializer: jobIds?.length
                ? () => jobIds.map((id) => `jobIds=${encodeURIComponent(id)}`).join('&')
                : undefined,
        }
    );
    return response.data.payload;
};

export const sendJobsEmailToCustomer = async (
    manifestId: string,
    jobIds: string[],
    emailAddress: string
): Promise<SendJobsEmailToCustomerResponse> => {
    const response: AxiosResponse<SuccessResponse<SendJobsEmailToCustomerResponse>> = await axiosClient.post(
        `${config.declarationsUrl}/manifest/${manifestId}/email-jobs`,
        {
            recipient: emailAddress,
            jobIds,
        }
    );
    return response.data.payload;
};

export const getJobEmailNotificationStatus = async (
    manifestId: string,
    jobId: string
): Promise<EmailStatusResponse> => {
    const response: AxiosResponse<SuccessResponse<EmailStatusResponse>> = await axiosClient.get(
        `${config.declarationsUrl}/manifest/${manifestId}/jobs/${jobId}/email-statuses`
    );
    return response.data.payload;
};

export const listJobsWithEmailNotificationStatuses = async (
    manifestId: string,
    params: GetJobsParams,
    options?: { signal?: AbortSignal }
): Promise<ListPayload<JobResponse>> => {
    const response: AxiosResponse<ListResponse<JobResponse>> = await axiosClient.get(
        `${config.declarationsUrl}/manifest/${manifestId}/jobs`,
        {
            params: { manifestId, ...params },
            signal: options?.signal,
        }
    );
    return response.data.payload;
};
